"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from "react";
import { MIN_QUERY, type SearchKind } from "./kinds";
import { SEARCH_SOURCES, mergeResults, type SearchResults } from "./searchKinds";

/** Four of each kind: the panel previews; the /search page holds the rest. */
const PANEL_COUNT = 4;

/**
 * The navbar panel's live search, over the same sources as /search.
 *
 * Kept from the panel's own rules: a 250ms debounce, and a generation
 * counter so a slow answer for "neo" can never overwrite the one for "neon"
 * (`searchGlobal` takes no AbortSignal). New: each source lands as soon as it
 * answers, and a failing source only empties its own kinds. The minimum is
 * now the backend's 3 characters — at 2 every keystroke drew a 422.
 */
export function usePanelSearch(q, open, lang) {
  const $ = _c(15);
  const [results, setResults] = useState(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [pending, setPending] = useState(t0);
  const gen = useRef(0);
  let t1;
  if ($[1] !== q) {
    t1 = q.trim();
    $[1] = q;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const text = t1;
  const active = open && text.length >= MIN_QUERY;
  let t2;
  let t3;
  if ($[3] !== active || $[4] !== lang || $[5] !== text) {
    t2 = () => {
      const mine = gen.current = gen.current + 1;
      if (!active) {
        setResults(null);
        setPending([]);
        return;
      }
      setPending(SEARCH_SOURCES.flatMap(_temp));
      const id = window.setTimeout(() => {
        const parts = [];
        SEARCH_SOURCES.forEach((source, i) => {
          source.run(text, lang, PANEL_COUNT).then(part => {
            parts[i] = part;
          }).catch(() => {
            parts[i] = undefined;
          }).finally(() => {
            if (mine !== gen.current) {
              return;
            }
            setResults(mergeResults(parts));
            setPending(kinds => kinds.filter(k => !source.kinds.includes(k)));
          });
        });
      }, 250);
      return () => window.clearTimeout(id);
    };
    t3 = [active, text, lang];
    $[3] = active;
    $[4] = lang;
    $[5] = text;
    $[6] = t2;
    $[7] = t3;
  } else {
    t2 = $[6];
    t3 = $[7];
  }
  useEffect(t2, t3);
  const t4 = active ? results : null;
  let t5;
  if ($[8] !== active || $[9] !== pending) {
    t5 = active ? pending : [];
    $[8] = active;
    $[9] = pending;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== active || $[12] !== t4 || $[13] !== t5) {
    t6 = {
      results: t4,
      pending: t5,
      active
    };
    $[11] = active;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
}
function _temp(s) {
  return s.kinds;
}